import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import HeaderNotifications from "./header/HeaderNotifications";
import HeaderSearch from "./header/HeaderSearch";
import HeaderUserMenu from "./header/HeaderUserMenu";

interface HeaderProps {
  getCurrentBreadcrumb: () => string;
  onMenuClick?: () => void;
}

function removeMngmt(input: string): string {
  return input.replace(/mngmt\./i, "");
}

type HeaderPanel = "search" | "notifications" | "user" | null;

const Header: React.FC<HeaderProps> = ({
  getCurrentBreadcrumb,
  onMenuClick,
}) => {
  const [openPanel, setOpenPanel] = useState<HeaderPanel>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (
        actionsRef.current &&
        !actionsRef.current.contains(event.target as Node)
      ) {
        setOpenPanel(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenPanel(null);
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpenPanel("search");
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="relative z-30 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur sm:px-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-3">
        <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-none">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              aria-label="Open navigation menu"
              className="-ml-1 shrink-0 rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            >
              <Menu size={22} />
            </button>
          )}
          <div className="min-w-0">
            <h2 className="truncate text-[18px] font-semibold leading-tight text-slate-900 sm:text-[20px]">
              {removeMngmt(getCurrentBreadcrumb())}
            </h2>
            <p className="hidden truncate text-[12px] text-slate-500 sm:block">
              {new Date().toLocaleDateString(undefined, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        </div>

        {/* On mobile the wrapper is display:contents so search drops to its own row */}
        <div
          ref={actionsRef}
          className="contents md:flex md:items-center md:gap-3"
        >
          <HeaderSearch
            isOpen={openPanel === "search"}
            onOpen={() => setOpenPanel("search")}
            onClose={() => setOpenPanel(null)}
            inputRef={searchInputRef}
          />
          <HeaderNotifications
            isOpen={openPanel === "notifications"}
            onToggle={() =>
              setOpenPanel((prev) =>
                prev === "notifications" ? null : "notifications"
              )
            }
            onClose={() => setOpenPanel(null)}
          />
          <HeaderUserMenu
            isOpen={openPanel === "user"}
            onToggle={() =>
              setOpenPanel((prev) => (prev === "user" ? null : "user"))
            }
            onClose={() => setOpenPanel(null)}
          />
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
